'use client';
import React, { useRef, useState } from 'react';
import { Button, ImageUploaderDropzone, ImageUploaderThumbnail } from '@ui';

interface ImageUploaderProps {
  maxFileSizeMB?: number;
  className?: string;
  id?: string;
}

const DEFAULT_MAX_MB = 10;

const ImageUploader: React.FC<ImageUploaderProps> = ({
  maxFileSizeMB = DEFAULT_MAX_MB,
  className,
  id,
}) => {
  const MAX_FILE_SIZE_MB = maxFileSizeMB;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

  const [images, setImages] = useState<Array<{ file: File; url: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null!);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const newImages: Array<{ file: File; url: string }> = [];
    Array.from(files).forEach((file) => {
      if (file.size <= MAX_FILE_SIZE && file.type.startsWith('image/')) {
        newImages.push({ file, url: URL.createObjectURL(file) });
      }
    });
    setImages((prev) => [...prev, ...newImages]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleRemove = (idx: number) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[idx].url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleClearAll = () => {
    images.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
  };

  const classes = `uploader${className ? ` ${className}` : ''}${
    dragActive ? ' is-drag-active' : ''
  }`;

  return (
    <div
      className={classes}
      id={id}
      data-uploader
      onClick={(e) => {
        const target = e.target as HTMLElement | null;
        if (target && target.closest('button')) return;
        handleBrowseClick();
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      <ImageUploaderDropzone fileInputRef={fileInputRef} handleInputChange={handleInputChange} />
      {images.length > 0 && (
        <>
          <div className='row' style={{ justifyContent: 'space-between' }}>
            <small className='hint' id='uploadInfo'>
              {images.length} file{images.length !== 1 ? 's' : ''} • max {MAX_FILE_SIZE_MB}MB each
            </small>
            <Button
              className='btn-sm btn-ghost'
              id='clearUploads'
              type='button'
              label='Clear all'
              onClick={handleClearAll}
            />
          </div>
          <div className='thumbnail-grid grid-3' id='thumbGrid'>
            {images.map((img, idx) => (
              <ImageUploaderThumbnail
                key={idx}
                file={img.file}
                url={img.url}
                onRemove={() => handleRemove(idx)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageUploader;
